// SelfVisitForm — modal to create / edit a Self Visit.
// Fields: Property (searchable), Visit Date & Time, Status, Remark.

import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { X, Save, Search, ChevronDown, Users, UserCheck } from "lucide-react";
import { toDateTimeLocal, fromDateTimeLocal } from "../../utils/datetime";
import "../../css/properties/propertyform.css";

/* Reusable searchable combobox (mirrors SiteVisitForm) */
function SearchableSelect({ value, onChange, options, placeholder = "— Select —", displayKey = "label", valueKey = "value" }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = options.filter((o) =>
    String(o[displayKey]).toLowerCase().includes(query.toLowerCase())
  );
  const selectedLabel = options.find((o) => o[valueKey] === value)?.[displayKey] || "";

  const handleSelect = (val) => { onChange(val); setOpen(false); setQuery(""); };

  return (
    <div className="searchable-select" ref={ref}>
      <div className={`ss-trigger ${open ? "open" : ""}`} onClick={() => setOpen((p) => !p)}>
        <span className={selectedLabel ? "ss-value" : "ss-placeholder"}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown size={14} className={`ss-chevron ${open ? "rotated" : ""}`} />
      </div>

      {open && (
        <div className="ss-dropdown">
          <div className="ss-search">
            <Search size={13} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <ul className="ss-list">
            {filtered.length === 0 && <li className="ss-empty">No results</li>}
            {filtered.map((o) => (
              <li
                key={o[valueKey]}
                className={`ss-item ${o[valueKey] === value ? "selected" : ""}`}
                onClick={() => handleSelect(o[valueKey])}
              >
                {o[displayKey]}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const EMPTY_FORM = {
  property_id:    "",
  scheduled_date: "",
  status:         "scheduled",
  visit_type:     "direct",   // "agent" | "direct"
  agent_email:    "",
  remark:         "",         // shown for any non-completed status
  feedback:       "",         // shown only when status === "completed"
};

export default function SelfVisitForm({ visit, properties, onSubmit, onCancel, isLoading }) {
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    if (visit) {
      setFormData({
        property_id:    typeof visit.property_id === "object" ? visit.property_id?._id : visit.property_id || "",
        scheduled_date: toDateTimeLocal(visit.scheduled_date),
        status:         visit.status || "scheduled",
        visit_type:     visit.visit_type || (visit.agent_email ? "agent" : "direct"),
        agent_email:    visit.agent_email || "",
        remark:         visit.remark || "",
        feedback:       visit.feedback || "",
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [visit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isCompleted = formData.status === "completed";

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.property_id || !formData.scheduled_date) {
      alert("Property and Visit Date & Time are required");
      return;
    }
    onSubmit({
      ...formData,
      scheduled_date: fromDateTimeLocal(formData.scheduled_date),
      // Clear agent_email if direct visit
      agent_email: formData.visit_type === "agent" ? formData.agent_email : "",
      // Keep only the field relevant to the current status
      feedback: isCompleted ? formData.feedback : "",
      remark:   isCompleted ? "" : formData.remark,
    });
  };

  const propertyOptions = properties.map((p) => ({
    value: p._id,
    label: p.propertyName + (p.location ? `, ${p.location}` : ""),
  }));

  const isAgent = formData.visit_type === "agent";

  return (
    <motion.div
      className="property-form-overlay"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <div className="property-form-card">
        {/* HEADER */}
        <div className="form-header">
          <h2>{visit ? "Edit Seller Visit" : "Add Seller Visit"}</h2>
          <button className="icon-btn" type="button" onClick={onCancel}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* PROPERTY */}
          <div className="form-group">
            <label>Property *</label>
            <SearchableSelect
              value={formData.property_id}
              onChange={(val) => setFormData((prev) => ({ ...prev, property_id: val }))}
              options={propertyOptions}
              placeholder="— Select Property —"
            />
          </div>

          {/* DATE & STATUS */}
          <div className="grid-2">
            <div className="form-group">
              <label>Visit Date & Time *</label>
              <input
                type="datetime-local"
                name="scheduled_date"
                value={formData.scheduled_date}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="rescheduled">Rescheduled</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* VISIT TYPE TOGGLE */}
          <div className="form-group">
            <label>Visit Type</label>
            <div className="visit-type-toggle">
              <button
                type="button"
                className={`toggle-btn ${formData.visit_type === "direct" ? "active" : ""}`}
                onClick={() => setFormData((prev) => ({ ...prev, visit_type: "direct", agent_email: "" }))}
              >
                <Users size={15} /> Direct Visit
              </button>
              <button
                type="button"
                className={`toggle-btn ${formData.visit_type === "agent" ? "active" : ""}`}
                onClick={() => setFormData((prev) => ({ ...prev, visit_type: "agent" }))}
              >
                <UserCheck size={15} /> Agent Visit
              </button>
            </div>
          </div>

          {/* AGENT EMAIL — only shown when Agent Visit is selected */}
          {isAgent && (
            <div className="form-group">
              <label>Agent Email *</label>
              <input
                type="email"
                name="agent_email"
                placeholder="agent@example.com"
                value={formData.agent_email}
                onChange={handleChange}
                required={isAgent}
              />
            </div>
          )}

          {/* FEEDBACK when completed, otherwise REMARK */}
          {isCompleted ? (
            <div className="form-group">
              <label>Feedback</label>
              <textarea
                rows="3"
                name="feedback"
                placeholder="Enter feedback about the completed visit..."
                value={formData.feedback}
                onChange={handleChange}
              />
            </div>
          ) : (
            <div className="form-group">
              <label>Remarks</label>
              <textarea
                rows="3"
                name="remark"
                placeholder="Enter remarks about this visit..."
                value={formData.remark}
                onChange={handleChange}
              />
            </div>
          )}

          {/* FOOTER */}
          <div className="form-footer">
            <button type="button" onClick={onCancel} className="btn-outline">Cancel</button>
            <button type="submit" disabled={isLoading} className="btn-primary">
              <Save size={16} /> {isLoading ? "Saving..." : "Save Seller Visit"}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
